import LegalLayout from '../components/LegalLayout';
import { useLanguage } from '../contexts/LanguageContext';

const VendorManagement = () => {
  const { language } = useLanguage();
  const isEs = language === 'es';

  return (
    <LegalLayout 
      title={isEs ? 'Gestión de Riesgos de Proveedores' : 'Vendor Risk Management'}
      subtitle={isEs ? 'Gestión de riesgos de terceros' : 'Third-party risk management'}
      icon="vendor"
    >
      <h2>{isEs ? 'Resumen' : 'Overview'}</h2>
      <p>
        {isEs 
          ? 'TeacherHubPro gestiona cuidadosamente las relaciones con proveedores externos para proteger los datos de los usuarios y mantener la calidad del servicio.'
          : 'TeacherHubPro carefully manages relationships with third-party vendors to protect user data and maintain service quality.'}
      </p>

      <h2>{isEs ? 'Proveedores Principales' : 'Key Vendors'}</h2>
      <p>
        {isEs 
          ? 'Trabajamos con proveedores selectos para proporcionar nuestros servicios:'
          : 'We work with select vendors to provide our services:'}
      </p>
      <ul>
        <li>
          <strong>{isEs ? 'Alojamiento en la nube:' : 'Cloud hosting:'}</strong>{' '}
          {isEs 
            ? 'Infraestructura de nube segura y escalable.'
            : 'Secure, scalable cloud infrastructure.'}
        </li>
        <li>
          <strong>{isEs ? 'Procesamiento de pagos (Stripe):' : 'Payment processing (Stripe):'}</strong>{' '}
          {isEs 
            ? 'Procesamiento de pagos seguro compatible con PCI.'
            : 'PCI-compliant secure payment processing.'}
        </li>
        <li>
          <strong>{isEs ? 'Proveedores de correo electrónico:' : 'Email providers:'}</strong>{' '}
          {isEs 
            ? 'Servicios de correo electrónico transaccional.'
            : 'Transactional email services.'}
        </li>
        <li>
          <strong>{isEs ? 'Analítica:' : 'Analytics:'}</strong>{' '}
          {isEs 
            ? 'Herramientas de análisis de uso para mejorar nuestros servicios.'
            : 'Usage analytics tools to improve our services.'}
        </li>
        <li>
          <strong>{isEs ? 'Servicios de IA:' : 'AI services:'}</strong>{' '}
          {isEs 
            ? 'Proveedores de IA reputados para funciones asistidas por IA.'
            : 'Reputable AI providers for AI-assisted features.'}
        </li>
      </ul>

      <h2>{isEs ? 'Acuerdos de Protección de Datos' : 'Data Protection Agreements'}</h2>
      <p>
        {isEs 
          ? 'Todos los proveedores que manejan datos de usuarios están sujetos a:'
          : 'All vendors handling user data are subject to:'}
      </p>
      <ul>
        <li>{isEs ? 'Acuerdos de protección de datos' : 'Data protection agreements'}</li>
        <li>{isEs ? 'Requisitos de confidencialidad' : 'Confidentiality requirements'}</li>
        <li>{isEs ? 'Estándares de seguridad' : 'Security standards'}</li>
        <li>{isEs ? 'Restricciones de uso de datos' : 'Data use restrictions'}</li>
      </ul>

      <h2>{isEs ? 'Proceso de Selección de Proveedores' : 'Vendor Selection Process'}</h2>
      <p>
        {isEs 
          ? 'Antes de seleccionar un proveedor, evaluamos:'
          : 'Before selecting a vendor, we evaluate:'}
      </p>
      <ul>
        <li>{isEs ? 'Prácticas de seguridad' : 'Security practices'}</li>
        <li>{isEs ? 'Postura de privacidad' : 'Privacy posture'}</li>
        <li>{isEs ? 'Alineación de cumplimiento' : 'Compliance alignment'}</li>
        <li>{isEs ? 'Historial y reputación' : 'Track record and reputation'}</li>
        <li>{isEs ? 'Estabilidad financiera' : 'Financial stability'}</li>
      </ul>

      <h2>{isEs ? 'Monitoreo Continuo' : 'Ongoing Monitoring'}</h2>
      <p>
        {isEs 
          ? 'Monitoreamos continuamente a los proveedores para:'
          : 'We continuously monitor vendors for:'}
      </p>
      <ul>
        <li>{isEs ? 'Cumplimiento con los acuerdos' : 'Compliance with agreements'}</li>
        <li>{isEs ? 'Incidentes de seguridad' : 'Security incidents'}</li>
        <li>{isEs ? 'Calidad del servicio' : 'Service quality'}</li>
        <li>{isEs ? 'Cambios en las prácticas' : 'Changes in practices'}</li>
      </ul>

      <h2>{isEs ? 'Derechos de Auditoría' : 'Audit Rights'}</h2>
      <p>
        {isEs 
          ? 'Mantenemos el derecho de auditar las prácticas de los proveedores según sea necesario para garantizar el cumplimiento de nuestros requisitos de seguridad y privacidad.'
          : 'We maintain the right to audit vendor practices as needed to ensure compliance with our security and privacy requirements.'}
      </p>

      <h2>{isEs ? 'Clasificación de Riesgo de Proveedores' : 'Vendor Risk Classification'}</h2>
      <p>
        {isEs
          ? 'Cada proveedor se clasifica según el nivel de riesgo que representa para los datos de usuarios, lo que determina la profundidad de la evaluación y la frecuencia de las revisiones:'
          : 'Each vendor is classified by the level of risk it poses to user data, which determines the depth of assessment and the frequency of reviews:'}
      </p>
      <ul>
        <li>
          <strong>{isEs ? 'Riesgo alto:' : 'High risk:'}</strong>{' '}
          {isEs
            ? 'Proveedores que procesan o almacenan datos de estudiantes o maestros (por ejemplo, alojamiento en la nube y servicios de IA). Requieren una evaluación de seguridad completa, un acuerdo de protección de datos firmado y una revisión anual documentada.'
            : 'Vendors that process or store student or teacher data (e.g., cloud hosting and AI services). These require a full security assessment, a signed data protection agreement, and a documented annual review.'}
        </li>
        <li>
          <strong>{isEs ? 'Riesgo medio:' : 'Medium risk:'}</strong>{' '}
          {isEs
            ? 'Proveedores con acceso limitado o temporal a datos (por ejemplo, correo transaccional). Requieren revisión de sus certificaciones de seguridad y cláusulas contractuales de confidencialidad.'
            : 'Vendors with limited or transient data access (e.g., transactional email). These require review of their security certifications and contractual confidentiality clauses.'}
        </li>
        <li>
          <strong>{isEs ? 'Riesgo bajo:' : 'Low risk:'}</strong>{' '}
          {isEs
            ? 'Proveedores sin acceso a datos de usuarios (por ejemplo, herramientas internas de desarrollo). Se revisan al momento de la contratación y ante cambios significativos del servicio.'
            : 'Vendors with no access to user data (e.g., internal development tooling). These are reviewed at onboarding and upon significant service changes.'}
        </li>
      </ul>

      <h2>{isEs ? 'Revisiones Periódicas' : 'Review Cadence'}</h2>
      <p>
        {isEs
          ? 'Los proveedores de riesgo alto se reevalúan al menos una vez al año. Todas las clasificaciones se revisan cuando un proveedor cambia materialmente su servicio, sufre un incidente de seguridad o modifica sus prácticas de manejo de datos. Los resultados de cada revisión se documentan y se conservan como parte de nuestro programa de cumplimiento.'
          : 'High-risk vendors are reassessed at least annually. All classifications are revisited whenever a vendor materially changes its service, experiences a security incident, or alters its data-handling practices. The results of each review are documented and retained as part of our compliance program.'}
      </p>

      <h2>{isEs ? 'Desvinculación de Proveedores' : 'Vendor Offboarding'}</h2>
      <p>
        {isEs
          ? 'Cuando finaliza una relación con un proveedor, exigimos la devolución o eliminación verificable de todos los datos de usuarios en su poder, revocamos todas las credenciales y accesos otorgados, y verificamos contractualmente que no se conserven copias. Este proceso se completa antes de cerrar formalmente la relación.'
          : 'When a vendor relationship ends, we require the return or verifiable deletion of all user data in their possession, revoke all granted credentials and access, and contractually confirm that no copies are retained. This process is completed before the relationship is formally closed.'}
      </p>

      <h2>{isEs ? 'Coordinación ante Incidentes' : 'Incident Coordination'}</h2>
      <p>
        {isEs
          ? 'Nuestros contratos exigen que los proveedores nos notifiquen sin demora injustificada sobre cualquier incidente de seguridad que afecte datos de TeacherHubPro. Al recibir una notificación, activamos nuestro propio plan de respuesta a incidentes, evaluamos el impacto en usuarios y escuelas, y comunicamos según nuestra Política de Notificación de Brechas.'
          : 'Our contracts require vendors to notify us without undue delay of any security incident affecting TeacherHubPro data. Upon notification, we activate our own incident response plan, assess the impact on users and schools, and communicate according to our Breach Notification Policy.'}
      </p>

      <h2>{isEs ? 'Contacto' : 'Contact'}</h2>
      <p>
        {isEs 
          ? 'Para preguntas sobre nuestras prácticas de gestión de proveedores:'
          : 'For questions about our vendor management practices:'}
      </p>
      <p>
        <a href="mailto:support@teacherhubpro.com" className="text-primary hover:underline">
          support@teacherhubpro.com
        </a>
      </p>
    </LegalLayout>
  );
};

export default VendorManagement;
